import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { LocalStorageService } from 'app/core/local/local-storage.service';

export interface ApiLogEvent {
  type: 'info' | 'log' | 'error' | 'close';
  line?: string;
  message?: string;
  file?: string;
  receivedAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class AdminLogsService {
  private eventSource?: EventSource;

  constructor(
    private zone: NgZone,
    private authService: AuthService,
    private localService: LocalStorageService
  ) {}

  streamApiLogs(): Observable<ApiLogEvent> {
    return new Observable<ApiLogEvent>((observer) => {
      const token = this.getToken();

      if (!token) {
        this.zone.run(() => {
          observer.next({
            type: 'error',
            message: 'No se encontró token de autenticación.',
            receivedAt: new Date(),
          });

          observer.complete();
        });

        return;
      }

      if (AuthUtils.isTokenExpired(token)) {
        this.zone.run(() => {
          observer.next({
            type: 'error',
            message: 'El token de autenticación ha caducado.',
            receivedAt: new Date(),
          });

          observer.complete();
        });

        return;
      }

      const url = `${environment.apiBaseUrl}/admin/api/stream?token=${encodeURIComponent(
        token
      )}`;

      this.close();

      this.eventSource = new EventSource(url);

      this.eventSource.addEventListener('open', () => {
        this.zone.run(() => {
          observer.next({
            type: 'info',
            message: 'Conectado al stream de logs',
            receivedAt: new Date(),
          });
        });
      });

      this.eventSource.addEventListener('info', (event: MessageEvent) => {
        this.zone.run(() => {
          const data = this.safeJson(event.data);

          observer.next({
            type: 'info',
            message: data?.message || 'Información del servidor',
            file: data?.file,
            receivedAt: new Date(),
          });
        });
      });

      this.eventSource.addEventListener('log', (event: MessageEvent) => {
        this.zone.run(() => {
          const data = this.safeJson(event.data);

          observer.next({
            type: 'log',
            line: data?.line || event.data,
            receivedAt: new Date(),
          });
        });
      });

      this.eventSource.addEventListener('error', () => {
        this.zone.run(() => {
          observer.next({
            type: 'error',
            message: 'Error o desconexión del stream',
            receivedAt: new Date(),
          });
        });
      });

      this.eventSource.addEventListener('close', (event: MessageEvent) => {
        this.zone.run(() => {
          const data = this.safeJson(event.data);

          observer.next({
            type: 'close',
            message: data?.message || 'Stream cerrado',
            receivedAt: new Date(),
          });

          observer.complete();
        });
      });

      return () => {
        this.close();
      };
    });
  }

  close(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = undefined;
    }
  }

  private getToken(): string | null {
    const token =
      this.authService.accessToken || this.localService.getItem('accessToken');

    if (!token || token === 'null' || token === 'undefined') {
      return null;
    }

    return String(token).replace(/^Bearer\s+/i, '');
  }

  private safeJson(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
}